import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { isFinishedFetching, ResourcesItem, ResourcesList } from 'redux-and-the-rest';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { makeStyles } from '@material-ui/core/styles';

// Select
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';

import { ApplicationState } from '../../../redux/types';
import { getOrFetchRewardCategories } from '../../../redux/reducers/rewardCategoryReducer';
import { RewardCategoryModel } from '../../../models/rewardCategoryModel';
import { getOrFetchGameScores } from '../../../redux/reducers/gameScore';
import { GameScoreModel } from '../../../models/gameScoreModel';
import { PercentageOffsets } from './types';
import RewardsGroup from './RewardsGroup';

import './RewardsExpandableWidget.scss';
import useWindowSize from '../../../hooks/useWindowSize';

const useStyles = makeStyles((theme) => ({
  select: {
    minWidth: '175px',
    fontSize: theme.typography.pxToRem(15),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  rewardItem: {
    minWidth: '50px',
    maxWidth: '15vw',
  },
}));

interface Props {
  gameScores: ResourcesList<GameScoreModel>,
  placementOfNewRewards: PercentageOffsets,
  rewardCategories: ResourcesList<RewardCategoryModel>;
}

const RewardsExpandableWidget = ({
  gameScores, placementOfNewRewards, rewardCategories,
}: Props) => {
  const classes = useStyles();
  const { isTouchDevice } = useWindowSize();

  const [claimedRewardGameScores, setClaimedRewardGameScores] = useState<ResourcesItem<GameScoreModel>[]>([]);

  useEffect(() => {
    if (isFinishedFetching(gameScores)) {
      setClaimedRewardGameScores(gameScores.items.filter(({ values: { chosenReward } }) => !!chosenReward));
    }
  }, [gameScores]);

  const defaultRewardCategory: RewardCategoryModel = {
    id: 'default',
    type: 'rewardCategory',
    name: 'All',
    rewards: [],
  };

  const [rewardFilter, setRewardFilter] = useState(defaultRewardCategory);

  const getRewardsByCategoryId = (categoryId: string) => rewardCategories.items
    .find(({ values: { id } }) => id === categoryId)?.values.rewards || [];

  const [expanded, setExpanded] = useState(false);
  const toggleExpansion = () => setExpanded(!expanded);

  /**
   * The order is correct. ExpandMoreIcon points down and ExpandLessIcon vice versa.
   */
  const ExpansionIcon = expanded ? ExpandMoreIcon : ExpandLessIcon;

  return (
    <div className="rewards-expandable-widget">
      <AccordionSummary
        className="expandable-widget-summary"
        expandIcon={(
          <ExpansionIcon
            style={{ color: 'white' }}
          />
        )}
        onClick={toggleExpansion}
      >
        <div className="expandable-widget-heading">
          {isTouchDevice ? 'TAP' : 'CLICK'}
          {' '}
          TO DECORATE
        </div>
      </AccordionSummary>

      <div className="expandable-widget-slider">
        <div className="panel bottom-align">
          <Accordion
            expanded={expanded}
            style={{ marginTop: '0px', maxHeight: 'inherit' }}
          >
            {/* We must set an accordion summary here, otherwise the accordion always stays expanded */}
            <AccordionSummary style={{ display: 'none' }} />

            <AccordionDetails className="accordion-contents">
              <FormControl className="decoration-filter">
                <InputLabel htmlFor="grouped-select" className={classes.select}>
                  DECORATION TYPE
                </InputLabel>

                <Select
                  className={classes.select}
                  labelId="grouped-select"
                  id="grouped-select"
                  defaultValue={defaultRewardCategory.id}
                  value={rewardFilter.id}
                  onChange={({ target: { value: rewardCategoryId } }) => {
                    if (rewardCategoryId === defaultRewardCategory.id) {
                      setRewardFilter(defaultRewardCategory);
                    } else {
                      const selectedCategory = rewardCategories
                        .items.find(({ values: { id } }) => id === rewardCategoryId);

                      if (selectedCategory) {
                        setRewardFilter(selectedCategory.values);
                      }
                    }
                  }}
                >
                  <MenuItem value={defaultRewardCategory.id} key={defaultRewardCategory.id}>
                    {defaultRewardCategory.name}
                  </MenuItem>

                  {
                    rewardCategories.items.map(({ values: { name, id } }) => (
                      <MenuItem value={id} key={id}>
                        {name}
                      </MenuItem>
                    ))
                  }
                </Select>

              </FormControl>

              <div className="reward-categories">
                {!(rewardFilter.id === defaultRewardCategory.id) && (
                  <RewardsGroup
                    rewards={getRewardsByCategoryId(rewardFilter.id)}
                    claimedRewardGameScores={claimedRewardGameScores}
                    placementOfNewRewards={placementOfNewRewards}
                  />
                )}

                {rewardFilter.id === defaultRewardCategory.id && (
                  rewardCategories.items.map(({
                    values: { id, rewards, name },
                  }) => (
                    <RewardsGroup
                      key={id}
                      groupName={name}
                      rewards={rewards}
                      claimedRewardGameScores={claimedRewardGameScores}
                      placementOfNewRewards={placementOfNewRewards}
                    />
                  ))
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (
  { rewardCategories, gameScores }: ApplicationState,
  { match: { params: { id: islandId } } }: RouteComponentProps<{ id: string; }>,
) => ({
  gameScores: getOrFetchGameScores(gameScores, { include: 'game,chosenReward,rewardsToChooseFrom', islandId }),
  rewardCategories: getOrFetchRewardCategories(rewardCategories, { include: 'rewards' }),
});

export default withRouter(connect(mapStateToProps)(RewardsExpandableWidget));
